import React, { useContext, useState } from "react";
import { ThemeContext } from "../../context/ThemeProvider";
import { SVG_IMAGES } from "../../assets";
import { BundleContext } from "../../context/BundleProvider";
import { GET_APP_DOMAIN_LIST } from "../../../app_constant";
import HTMLContent from "../../components/HTMLContent";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faLayerGroup, 
  faTag,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';
import { getFlashCardsIconBg } from "../../utils/themeUtils";

const FlashCardTest = ({ item, isVisible, onViewMore, title }) => {
  const { theme, currentTheme } = useContext(ThemeContext);
  const { selectedCertificate } = useContext(BundleContext);
  
  // Helper function to get icon background color
  const getIconBg = () => {
    return getFlashCardsIconBg(theme, currentTheme);
  }

  // Helper function for text color
  const getTextColor = () => {
    return currentTheme === 'light' 
      ? theme?.text?.light
      : theme?.text?.dark;
  };

  function getTopicId() {
    const APP_DOMAIN_LIST = GET_APP_DOMAIN_LIST(selectedCertificate);
    const array = APP_DOMAIN_LIST.find((e) => e.id == item?.topic_id);
    return array?.domain_name ?? "";
  }
  
  const topicName = getTopicId();

  // Animations
  const contentVariants = {
    hidden: { opacity: 0, height: 0 },
    visible: { 
      opacity: 1, 
      height: "auto",
      transition: { 
        duration: 0.3,
        ease: "easeInOut"
      }
    },
    exit: { 
      opacity: 0, 
      height: 0,
      transition: { 
        duration: 0.2,
        ease: "easeInOut"
      }
    }
  };

  const buttonVariants = {
    hover: { 
      scale: 1.05,
      boxShadow: "0px 5px 15px rgba(0,0,0,0.1)",
      transition: { type: "spring", stiffness: 400, damping: 10 }
    },
    tap: { scale: 0.95 }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Flashcard Question */}
      <div 
        className="flex-1 rounded-lg p-6 relative overflow-hidden"
        style={{ 
          
          minHeight: "180px"
        }}
      >
        <div className="relative z-10">
          <div className="flex flex-col items-center">
            <HTMLContent 
              htmlContent={item?.title}
              style={{ color: getTextColor() }}
              className="text-center text-xl font-medium mb-4"
            />
          </div>

          {/* Background circles for visual effect */}
          <div className="absolute top-0 left-0 z-0 opacity-20">
            <img src={SVG_IMAGES?.Circle1} alt="" />
          </div>
          <div className="absolute top-0 left-0 z-0 opacity-10">
            <img src={SVG_IMAGES?.Circle2} alt="" />
          </div>
          <div className="absolute top-0 right-0 z-0 opacity-15">
            <img src={SVG_IMAGES?.Circle3} alt="" />
          </div>
          
          <AnimatePresence>
            {isVisible && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={contentVariants}
                className="mt-6 pt-6 border-t border-white border-opacity-30"
              >
                <div className="flex flex-col items-center">
                  <HTMLContent 
                    htmlContent={item?.desc}
                    style={{ color: getTextColor() }}
                    className="text-center mb-4"
                  />
                  
                  {topicName !== "" && (
                    <div className="mt-4 py-2 px-4 bg-white bg-opacity-20 rounded-full">
                      <div className="flex items-center">
                        <FontAwesomeIcon 
                          icon={faTag} 
                          className="text-white text-opacity-70 mr-2 text-sm" 
                          style={{ color: getIconBg() }}
                        />
                        <span className="text-white text-opacity-90 text-sm italic" style={{ color: getTextColor() }}>
                          {topicName}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Show Answer Button */}
      {!isVisible && (
        <div className="flex justify-center mt-5">
          <motion.button
            variants={buttonVariants}
            whileHover="hover"
            whileTap="tap"
            onClick={onViewMore}
            className="bg-white dark:bg-gray-800 text-sm font-medium py-2 px-6 rounded-full shadow-md flex items-center"
            style={{ backgroundColor: getIconBg(), color: getTextColor() }}
          >
            <FontAwesomeIcon icon={faEye} className="mr-2" />
            Show Answer
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default FlashCardTest;